// OTE verification checklist — revision R1 (Oct 2026). FIXED: each report freezes this revision, so later wording
// changes never alter old reports. KEEP IN STEP with supabase/functions/_shared/oteChecklist.ts (same items).
//
// kind:  'result'   Satisfactory / Not Satisfactory (no N/A)
//        'parts'    sub-points, each Satisfactory / Not Satisfactory (or N/A where allowed); Satisfactory when all are
//        'factors'  sub-points, each Yes / No / N/A; Satisfactory only when each is Yes or N/A
//        'computed' answered from the activity table (never ticked)
// B4 is answered by the auditor AND needs every activity to be LOW (computed part).
export const OTE_CHECKLIST_REVISION = 'R1'

export const OTE_SECTIONS = [
  { code: 'A', title: 'Risk Assessment' },
  { code: 'B', title: 'Risk Evaluation and Controls' },
  { code: 'C', title: 'Overtime-Specific Risk Assessment' },
  { code: 'D', title: 'Communication' },
  { code: 'E', title: 'MOM OTE Eligibility' },
]

export const OTE_ITEMS = [
  { code: 'A1', section: 'A', title: 'Risk Assessment established', kind: 'result',
    verify: 'An appropriate Risk Assessment has been developed for the workplace and the OTE work activities.',
    evidence: 'The Risk Assessment / Risk Register (required).' },
  { code: 'A2', section: 'A', title: 'Coverage of OTE work activities', kind: 'computed',
    verify: 'Risk Assessments have been conducted and documented for ALL work activities included in the OTE application.',
    evidence: 'The RA sections covering each activity (referenced on each activity).' },
  { code: 'A3', section: 'A', title: 'Risk Assessment governance', kind: 'parts',
    verify: 'The RA was conducted by appropriate / competent persons, is documented and dated, and has management approval where applicable.',
    parts: [
      { key: 'competent', label: 'Conducted by appropriate / competent persons' },
      { key: 'dated', label: 'Documented and dated' },
      { key: 'approval', label: 'Management approval / endorsement', na: true },
    ],
    evidence: 'RA team list or competency records; management approval page; dated RA.' },
  { code: 'B4', section: 'B', title: 'Hazard identification and risk evaluation', kind: 'result', needsLow: true,
    verify: 'Relevant WSH hazards are identified, risks are appropriately evaluated, the risk evaluation matrix / method is properly defined, and the final risk level of every activity covered by the endorsement is LOW.',
    evidence: 'Risk matrix / evaluation method; RA pages showing the evaluation.' },
  { code: 'B5', section: 'B', title: 'Risk control measures', kind: 'result', swp: true,
    verify: 'Relevant risk controls are identified, the controls stated in the RA have actually been implemented where applicable, and the final risk level takes the implemented controls into account.',
    evidence: 'SWPs / procedures for the activities (expected — attach, or give the reason there is none); implementation photos of the controls.' },
  { code: 'C6', section: 'C', title: 'Extended working hours', kind: 'result',
    verify: 'The Risk Assessment has considered the length of extended working hours, the type of work and the workload of employees.',
    evidence: 'Shift, roster or working-hours information; RA section on extended hours.' },
  { code: 'C7', section: 'C', title: 'Specific OTE risk factors', kind: 'factors',
    verify: 'Where applicable, the RA considers adjusted exposure limits, heat stress and manual material lifting.',
    parts: [
      { key: 'chemical', label: 'Adjusted chemical exposure limits' },
      { key: 'noise', label: 'Adjusted noise exposure limits' },
      { key: 'heat', label: 'Heat stress' },
      { key: 'lifting', label: 'Manual material lifting' },
    ],
    evidence: 'Chemical or noise exposure monitoring, heat stress or manual handling assessments — where applicable.' },
  { code: 'D8', section: 'D', title: 'Communication of risks', kind: 'result',
    verify: 'Relevant workplace risks and controls have been communicated to affected employees / stakeholders.',
    evidence: 'RM briefing / training attendance; toolbox or communication records.' },
  { code: 'E9', section: 'E', title: 'Annex A check', kind: 'computed',
    verify: 'None of the work activities being endorsed is prohibited / excluded under Annex A of MOM\'s OTE application requirements.',
    evidence: 'None — the auditor confirms each activity against MOM Annex A.' },
]

const S = 'Satisfactory', NS = 'Not Satisfactory'
// One item's result. answers: { [code]: { result, factors } }; rows: the activities still in scope.
export function itemResult(item, answers, rows) {
  const a = answers[item.code] || {}
  if (item.code === 'A2') return rows.length && rows.every(r => String(r.ra_reference || '').trim()) ? S : NS
  if (item.code === 'E9') return rows.length && rows.every(r => r.annex_a === false) ? S : NS
  if (item.kind === 'parts') {
    const f = a.factors || {}
    if (item.parts.some(p => !f[p.key])) return null
    return item.parts.every(p => f[p.key] === S || (p.na && f[p.key] === 'N/A')) ? S : NS
  }
  if (item.kind === 'factors') {
    const f = a.factors || {}
    if (item.parts.some(p => !f[p.key])) return null
    return item.parts.every(p => f[p.key] === 'Yes' || f[p.key] === 'N/A') ? S : NS
  }
  if (!a.result) return null
  if (item.needsLow && a.result === S) return rows.length && rows.every(r => r.risk_level === 'Low') ? S : NS
  return a.result
}
